import { FC, useMemo } from 'react'
import { useDispatch, useSelector } from 'utils/hooks'
import { Flex, Button } from '@jisr-hr/ds'
import DSIcon from 'components/DSIcon'
import i18n from 'translations/i18n'
import { labels } from 'utils/whitelabel'
import { required } from 'components/global/form/FormValidations'
import { PaymentConfigurationType } from 'redux/setting/payrollManagement/paymentMethods/reducer'
import {
  offlinePaymentAddEmployees,
  offlinePaymentDetailsLoad,
  paymentMethodCreate,
  paymentMethodLoad,
  paymentMethodSetDefault,
  updateSinglePaymentOfList,
} from 'redux/setting/payrollManagement/paymentMethods/actionCreators'
import { Form, MultiSelectField, SelectField } from 'components/final-form'
import { usePaymentMethod } from '../../components/usePaymentMethods'
import BanksSelectMenu from '../MudadPaymentMethod/AuthorizePaymentAdmins/BanksSelectMenu'

type EmployeeTempType = {
  id: number
  full_name_i18n: string
}

type FormPayload = {
  bank_account_id: number
  employees: EmployeeTempType[]
}

type OfflineContentType = {
  onClose: () => void
  afterSubmit?: () => void
}

const OfflineContent: FC<OfflineContentType> = (props) => {
  const dispatch = useDispatch()
  const paymentCtx = usePaymentMethod()
  const { selectedPaymentMethod, setupModalOpen, isOnBoarding } = paymentCtx

  const employees = useSelector((s) => s.eligibleEmployees.employee_all)
  const onboardingEmployees = useSelector((s) => s?.reviewingEmployee.employees)

  const details = useSelector((s) => s.paymentMethods.offline_payment_details)
  const fetching = useSelector((s) => s.paymentMethods.fetchingShowMethod)
  const loading = useSelector((s) => s.paymentMethods.loading)
  const paymentMethod = useSelector((s) => s.paymentMethods.current_payment_method)
  const offline = paymentMethod?.offline_payment_configuration
  const mudad = paymentMethod?.mudad_payment_configuration

  const banksList = useMemo(() => {
    return selectedPaymentMethod?.bank_accounts.map((bank) => {
      return {
        ...bank,
        bank_name: `${bank.bank_name} - ${bank.iban}`,
      }
    })
  }, [selectedPaymentMethod?.bank_accounts])

  const initState = useMemo(() => {
    return {
      employees: details?.employees.map((it) => ({ ...it, name: it.name_i18n })),
      bank_account_id: details?.bank_account.id,
    }
  }, [details])

  const onSubmit = async (data: FormPayload): Promise<void> => {
    if (!selectedPaymentMethod) return
    const { id } = selectedPaymentMethod
    await dispatch(
      offlinePaymentAddEmployees(id, {
        bank_account_id: data.bank_account_id,
        commercial_registration_id: id,
        employee_ids: data.employees?.map((it) => it.id),
      }),
    )

    // ? @note incase it's new offline payment we have to create it
    if (!offline) {
      const payload = {
        bank_account_id: data.bank_account_id,
        payment_mode: 'offline',
      } as const
      const paymentConfig = await dispatch(paymentMethodCreate(id, payload))

      // ? @note incase mudad payment id inactive, we have to set the offline as default
      if (mudad?.status !== 'active') {
        const payment_id = paymentConfig.value.data.data.payrun_payment_configuration?.id as number
        await dispatch(
          paymentMethodSetDefault({
            cr_id: id,
            id: payment_id,
          }),
        )
      }
    }

    const res = await dispatch(paymentMethodLoad(id))
    const payment = res.value.data.data.commercial_registration as PaymentConfigurationType

    await dispatch(updateSinglePaymentOfList(payment))
    props.afterSubmit?.()

    if (!setupModalOpen) paymentCtx.setSelectedPaymentMethod(null)

    paymentCtx.setMethodModalOpen(null)
  }

  const handleBankChange = async (bank_id: number): Promise<void> => {
    await dispatch(
      offlinePaymentDetailsLoad({
        cr_id: Number(selectedPaymentMethod?.id),
        id: bank_id,
      }),
    )
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={offline ? initState : {}}
    >
      {({ handleSubmit, valid }): JSX.Element => {
        return (
          <form
            onSubmit={handleSubmit}
            style={{ padding: 24 }}
          >
            <SelectField
              // @ts-expect-error // select field needs to be migrated to TS
              label={i18n.t('bank_account')}
              name="bank_account_id"
              options={banksList}
              labelKey="bank_name"
              valueKey="id"
              placeholder={i18n.t('bank_account')}
              onChange={handleBankChange}
              style={{ marginBottom: 24 }}
              components={!isOnBoarding && { Menu: BanksSelectMenu }}
              onAddBankClick={(): void => {
                paymentCtx.handleManagementPaymentOpen(selectedPaymentMethod)
              }}
            />
            <MultiSelectField
              name="employees"
              label={i18n.t('employees')}
              options={isOnBoarding ? onboardingEmployees : employees}
              disabled={fetching}
              style={{ width: 'fit-content', minWidth: '100%', marginBottom: 10 }}
              maxMenuHeight={200}
              valueKey="id"
              placeholder={i18n.t('employees')}
              labelKey={isOnBoarding ? 'full_name_en' : 'name'}
              onChange={undefined}
              validate={required}
              testId={undefined}
            />
            {labels.name === 'Jisr' && (
              <Button
                size="sm"
                label={i18n.t('have_sabb_bank_account_check_integration_steps')}
                variant="tertiary"
                type="button"
                onClick={(): void => {
                  window.open(
                    'https://jisr.zendesk.com/hc/ar-sa/articles/13753277808785-%D8%A5%D8%B9%D8%AF%D8%A7%D8%AF-%D8%AF%D9%81%D8%B9-%D8%A7%D9%84%D8%B1%D9%88%D8%A7%D8%AA%D8%A8-%D8%B9%D9%86-%D8%B7%D8%B1%D9%8A%D9%82-%D8%AA%D9%81%D8%B9%D9%8A%D9%84-%D8%A7%D9%84%D8%B1%D8%A8%D8%B7-%D8%A7%D9%84%D9%85%D8%A8%D8%A7%D8%B4%D8%B1-%D9%85%D8%B9-%D8%A8%D9%86%D9%83-%D8%A7%D9%84%D8%A3%D9%88%D9%84',
                  )
                }}
                iconTrailing={<DSIcon name="link-external-01" />}
              />
            )}

            <Flex
              itemsCenter
              justifyEnd
              style={{ gap: 16, marginTop: 200 }}
            >
              <Button
                size="sm"
                label={i18n.t('cancel')}
                variant="secondary"
                onClick={props.onClose}
              />
              <Button
                disabled={fetching || loading || !valid}
                size="sm"
                label={i18n.t('save')}
                type="submit"
              />
            </Flex>
          </form>
        )
      }}
    </Form>
  )
}

export default OfflineContent
